import { HugeiconsIcon } from '@hugeicons/react';
import { LinkSquare01Icon, SquareArrowUpRightIcon } from '@hugeicons/core-free-icons';
import { EXPORT_PROVIDERS, getExportProviderLabel, sanitizeUrl, type ExportProvider } from '@overlayqa/shared';
import type { Ticket } from '@overlayqa/shared';
import { log } from '@/lib/logger';

interface ExportedToSectionProps {
  ticket: Ticket;
}

// Export providers take the shared label; Slack is a notification target, not an export provider.
const providerLabel = (provider: string): string =>
  (EXPORT_PROVIDERS as readonly string[]).includes(provider)
    ? getExportProviderLabel(provider as ExportProvider)
    : provider === 'slack' ? 'Slack' : provider;

/** Brand color CSS variables defined in styles/index.css */
const PROVIDER_COLOR_VAR: Record<string, string> = {
  jira: 'var(--color-jira)',
  linear: 'var(--color-linear)',
  notion: 'var(--color-notion)',
  asana: 'var(--color-asana)',
};

/**
 * Displays the external locations this issue has been synced to
 * (Jira / Linear / Notion). Only renders when at least one export
 * exists. Mirrors the extension's per-pin "exported" badges so the
 * dashboard surfaces the same information.
 */
export function ExportedToSection({ ticket }: ExportedToSectionProps) {
  // Defensive: exportedTo is a jsonb column — a malformed row (e.g. `{}` instead
  // of `[]`) must degrade to "no exports", never crash the page (2026-07-14).
  const exports = Array.isArray(ticket.exportedTo) ? ticket.exportedTo : [];
  if (exports.length === 0) return null;

  log.info('[ExportedToSection] Rendering exports', { count: exports.length });

  return (
    <section id="issue-exports" className="card-sm">
      <h2 className="section-label mb-3 flex items-center gap-2">
        <HugeiconsIcon
          icon={SquareArrowUpRightIcon}
          size={16}
          strokeWidth={2}
          color="currentColor"
          className="w-4 h-4 text-gray-400"
          aria-hidden="true"
        />
        Synced to
      </h2>
      <ul className="mt-4 space-y-2">
        {exports.map((exp) => {
          const label = providerLabel(exp.provider);
          const colorVar = PROVIDER_COLOR_VAR[exp.provider];
          const safeUrl = sanitizeUrl(exp.url);
          const showLink = safeUrl !== null;
          const externalKey = exp.externalKey || exp.externalId;

          return (
            <li
              key={`${exp.provider}-${exp.externalId}`}
              className="flex items-center justify-between gap-3 text-sm"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span
                  aria-hidden="true"
                  className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                  style={colorVar ? { backgroundColor: colorVar } : { backgroundColor: 'var(--color-gray-400)' }}
                />
                <span className="text-gray-700 font-medium flex-shrink-0">{label}</span>
                <span className="text-gray-500 truncate" title={externalKey}>
                  {externalKey}
                </span>
              </div>
              {showLink && (
                <a
                  href={safeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-500 hover:underline inline-flex items-center gap-1 text-xs flex-shrink-0"
                >
                  <span className="text-cap-trim">Open</span>
                  <HugeiconsIcon
                    icon={LinkSquare01Icon}
                    size={12}
                    strokeWidth={2}
                    color="currentColor"
                    className="w-3 h-3"
                    aria-hidden="true"
                  />
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
